import React, {forwardRef, useImperativeHandle, useState} from "react";
import {Upload} from "antd";
import Button from "@common/Button";
import {UploadChangeParam} from "antd/es/upload";
import {InboxOutlined} from "@ant-design/icons";
import Alert from "@common/Alert";
import {Clazz, ERRORS, REMOVE_TYPES, User} from "./BulkClassUpdate";
import UploadDiagramImg from "./UploadDiagramImg";
import axios from "@common/axios";

export interface UploadClassListRef {
    checkNextStep: () => void;
}

interface Props {
    hasData: boolean;
    hydrateProcessedData: ({users, classes, schoolClasses}: {users: Record<number, User>, classes: Record<string, Clazz>, schoolClasses: Record<number, object>, validUserCount: number, invalidUserCount: number}) => void;
    startImport: () => void;
}

const UploadClassList = forwardRef<UploadClassListRef, Props>((props, ref) => {
    const {hasData = false, hydrateProcessedData, startImport} = props;
    const [uploadFileError, setUploadFileError] = useState<null | string>(null);

    const handleUploadChange = (info: UploadChangeParam): void => {
        const {status, response} = info.file;
        if (status === 'done') {
            if (response?.error) {
                setUploadFileError(response.error);
                hydrateProcessedData({users: {}, classes: {}, schoolClasses: {}, validUserCount: 0, invalidUserCount: 0});
            } else {
                setUploadFileError(null);
                hydrateProcessedData(response);
            }
        } else if (status === 'error') {
            console.error('Upload failed. Response:', response);
            setUploadFileError("Upload failed. Please try again.");
        }
    }

    const checkNextStep = (): void => {
        if (uploadFileError == null && hasData) {
            startImport();
        }
    }

    const handleDownloadUserSheet = (): void => {
        axios.get('/web/ui/sc/exportSchoolUsers', {responseType: 'arraybuffer'})
            .then(response => {
                const blob = new Blob([response.data], { type: 'text/csv' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = 'class_update_spreadsheet_template.csv';
                link.click();
            });
    }

    useImperativeHandle(ref, () => ({
        checkNextStep
    }))

    return (
        <>
            <h1>Upload Class List</h1>
            <p>
                To use the class importer you need to create a class spreadsheet with your student or teacher email addresses, and the classes you want to create or assign. Your class spreadsheet should meet the following requirements:
            </p>
            <UploadDiagramImg style={{width: '100%'}} />
            <div className="margin-t--l">
                <Upload.Dragger
                    action="/web/ui/sc/parseBulkClassUpdate"
                    onChange={handleUploadChange}
                    headers={{contentType: 'multipart/form-data'}}
                    name="file"
                    accept=".csv"
                    maxCount={1}
                    style={uploadFileError != null ? {border: '1px solid var(--error-colour)'} : {}}
                    showUploadList={
                        uploadFileError == null
                            ? { showRemoveIcon: true }
                            : false
                    }
                >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                        Click or drag file to this area to upload
                    </p>
                    <p className="ant-upload-hint">
                        File types accepted are .csv
                    </p>
                </Upload.Dragger>
            </div>
            {uploadFileError != null && (
                <Alert
                    type="error"
                    showIcon
                    description={uploadFileError}
                    className="margin-t--m"
                />
            )}
            <hr className="margin-t--l"/>
            <h2 className="margin-t--l">Need a spreadsheet to get started?</h2>
            <p>Download a list of users in your school and their current classes which can be modified and uploaded.</p>
            <Button className="margin-t--m" type="outline" onClick={handleDownloadUserSheet}>Download Users Sheet</Button>
        </>
    )
});

export default UploadClassList;